/**
 * Social Media Helper utilities
 * Constants and utility functions for social media management
 */

// Platform character limits
export const CHARACTER_LIMITS = {
    youtube: 5000,
    facebook: 63206,
    instagram: 2200,
    linkedin: 3000,
};

// Platform colors for UI consistency
export const PLATFORM_COLORS = {
    youtube: {
        bg: 'bg-red-100',
        text: 'text-red-600',
        border: 'border-red-200',
        dark: {
            bg: 'dark:bg-red-900/30',
            text: 'dark:text-red-400',
            border: 'dark:border-red-800'
        }
    },
    facebook: {
        bg: 'bg-blue-100',
        text: 'text-blue-600',
        border: 'border-blue-200',
        dark: {
            bg: 'dark:bg-blue-900/30',
            text: 'dark:text-blue-400',
            border: 'dark:border-blue-800'
        }
    },
    instagram: {
        bg: 'bg-pink-100',
        text: 'text-pink-600',
        border: 'border-pink-200',
        dark: {
            bg: 'dark:bg-pink-900/30',
            text: 'dark:text-pink-400',
            border: 'dark:border-pink-800'
        }
    },
    linkedin: {
        bg: 'bg-sky-100',
        text: 'text-sky-700',
        border: 'border-sky-200',
        dark: {
            bg: 'dark:bg-sky-900/30',
            text: 'dark:text-sky-400',
            border: 'dark:border-sky-800'
        }
    },
};

// Post status colors
export const STATUS_COLORS = {
    draft: { bg: 'bg-slate-100', text: 'text-slate-600', dark: { bg: 'dark:bg-slate-800', text: 'dark:text-slate-400' } },
    scheduled: { bg: 'bg-sky-100', text: 'text-sky-600', dark: { bg: 'dark:bg-sky-900/30', text: 'dark:text-sky-400' } },
    publishing: { bg: 'bg-amber-100', text: 'text-amber-600', dark: { bg: 'dark:bg-amber-900/30', text: 'dark:text-amber-400' } },
    published: { bg: 'bg-emerald-100', text: 'text-emerald-600', dark: { bg: 'dark:bg-emerald-900/30', text: 'dark:text-emerald-400' } },
    failed: { bg: 'bg-red-100', text: 'text-red-600', dark: { bg: 'dark:bg-red-900/30', text: 'dark:text-red-400' } },
    cancelled: { bg: 'bg-slate-100', text: 'text-slate-500', dark: { bg: 'dark:bg-slate-800', text: 'dark:text-slate-500' } },
};

// Platform metadata
export const PLATFORMS = {
    youtube: {
        name: 'YouTube',
        icon: 'Youtube',
        supportsVideo: true,
        supportsImages: true,
        requiresTitle: true,
        hasPrivacySettings: true,
    },
    facebook: {
        name: 'Facebook',
        icon: 'Facebook',
        supportsVideo: true,
        supportsImages: true,
        requiresTitle: false,
        hasPrivacySettings: true,
    },
    instagram: {
        name: 'Instagram',
        icon: 'Instagram',
        supportsVideo: true,
        supportsImages: true,
        requiresTitle: false,
        hasPrivacySettings: false,
    },
    linkedin: {
        name: 'LinkedIn',
        icon: 'Linkedin',
        supportsVideo: true,
        supportsImages: true,
        requiresTitle: false,
        hasPrivacySettings: true,
    },
};

// Integration limits by tier
export const INTEGRATION_LIMITS = {
    free: 0,
    starter: 0,
    pro: 0,
    ultra: 5,
    enterprise: 25,
};

/**
 * Get the lowest character limit for selected platforms
 * @param {string[]} selectedPlatforms - Array of platform IDs
 * @returns {number} The minimum character limit
 */
export const getCharacterLimit = (selectedPlatforms) => {
    if (!selectedPlatforms || selectedPlatforms.length === 0) {
        return Math.min(...Object.values(CHARACTER_LIMITS));
    }

    const limits = selectedPlatforms
        .filter(p => CHARACTER_LIMITS[p])
        .map(p => CHARACTER_LIMITS[p]);

    return limits.length > 0 ? Math.min(...limits) : 2200; // Default to Instagram limit
};

/**
 * Format scheduled date for display
 * @param {string} isoString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatScheduledDate = (isoString) => {
    if (!isoString) return '';

    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();

    const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });

    if (isToday) {
        return `Today at ${timeStr}`;
    } else if (isTomorrow) {
        return `Tomorrow at ${timeStr}`;
    } else {
        const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
        return `${dateStr} at ${timeStr}`;
    }
};

/**
 * Validate post content against platform limits
 * @param {string} content - Post content
 * @param {string[]} platforms - Array of platform IDs
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validatePostContent = (content, platforms) => {
    const errors = [];

    if (!content || content.trim().length === 0) {
        errors.push('Content cannot be empty');
        return { valid: false, errors };
    }

    if (!platforms || platforms.length === 0) {
        errors.push('At least one platform must be selected');
        return { valid: false, errors };
    }

    for (const platform of platforms) {
        const limit = CHARACTER_LIMITS[platform];
        if (limit && content.length > limit) {
            errors.push(`Content exceeds ${PLATFORMS[platform]?.name || platform} limit of ${limit.toLocaleString()} characters`);
        }
    }

    return { valid: errors.length === 0, errors };
};

/**
 * Get platform color classes as a combined string
 * @param {string} platform - Platform ID
 * @returns {string} Combined Tailwind class string
 */
export const getPlatformColorClasses = (platform) => {
    const colors = PLATFORM_COLORS[platform];
    if (!colors) return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';

    return `${colors.bg} ${colors.text} ${colors.dark.bg} ${colors.dark.text}`;
};

/**
 * Get status color classes as a combined string
 * @param {string} status - Post status
 * @returns {string} Combined Tailwind class string
 */
export const getStatusColorClasses = (status) => {
    const colors = STATUS_COLORS[status?.toLowerCase()];
    if (!colors) return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';

    return `${colors.bg} ${colors.text} ${colors.dark.bg} ${colors.dark.text}`;
};

/**
 * Truncate content for preview
 * @param {string} content - Full content
 * @param {number} maxLength - Maximum length (default 150)
 * @returns {string} Truncated content with ellipsis if needed
 */
export const truncateContent = (content, maxLength = 150) => {
    if (!content || content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
};

/**
 * Get display name for a platform
 * @param {string} platform - Platform ID
 * @returns {string} Display name
 */
export const getPlatformDisplayName = (platform) => {
    return PLATFORMS[platform]?.name || platform;
};
